import { Controller } from 'react-hook-form'
import type { UseFormReturn } from 'react-hook-form'
import { cn, Heading } from '@quickmd-llc/design-system'
import type { LicenseFormValues, LicenseFormVisitType } from '@/pages/member/license-form/LicenseFormSchema'

const VISIBILITY_OPTIONS = [
  { label: 'No bookings', value: 'none' },
  { label: 'Repeat patients', value: 'repeat' },
  { label: 'All patients', value: 'all' },
]

function getBulkVisibility(visitTypes: LicenseFormVisitType[]): string {
  const selected = visitTypes.filter((vt) => vt.isSelected)
  if (selected.length === 0) return 'all'
  const first = selected[0]?.visibilityOverride
  const allSame = selected.every((vt) => vt.visibilityOverride === first)
  return allSame ? (first ?? 'all') : 'custom'
}

type VisibilityButtonGroupProps = {
  value: string
  onChange: (value: string) => void
  showCustom?: boolean
}

function VisibilityButtonGroup({ value, onChange, showCustom }: VisibilityButtonGroupProps) {
  const options = [...(showCustom ? [{ label: 'Custom', value: 'custom' }] : []), ...VISIBILITY_OPTIONS]
  return (
    <div className='flex gap-sp-1'>
      {options.map((opt) => {
        const isActive = value === opt.value
        const isCustom = opt.value === 'custom'
        return (
          <button
            key={opt.value}
            type='button'
            onClick={() => {
              if (!isCustom) onChange(opt.value)
            }}
            className={cn(
              'flex min-w-[80px] items-center justify-center gap-[2px] rounded-full border px-sp-1 py-[3px] text-xs font-semibold tracking-[0.12px]',
              isActive ?
                'border-border-highlight bg-text-highlight text-white'
              : 'border-shade-10 bg-coastal-blue-10 text-text-highlight',
              isCustom ? 'cursor-default' : 'cursor-pointer',
            )}>
            {isActive && !isCustom ?
              <svg width='12' height='12' viewBox='0 0 12 12' fill='currentColor'>
                <path d='M10 3.5a.5.5 0 0 1 0 .7l-4.5 4.5a.5.5 0 0 1-.7 0L2.5 6.4a.5.5 0 1 1 .7-.7L5 7.4l4.1-4.1a.5.5 0 0 1 .7 0z' />
              </svg>
            : null}
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

type Props = {
  licenseForm: UseFormReturn<LicenseFormValues>
}

/**
 * "Booking access settings" section (Provider only): the `visitTypes` access table driven by the
 * RHF `Controller`. The orchestrator owns the `useForm` instance and passes it in. Already-Tailwind
 * markup is carried over verbatim (this section had no `nm-*` SCSS classes).
 */
export const BookingAccessSection = ({ licenseForm }: Props) => (
  <Controller
    name='visitTypes'
    control={licenseForm.control}
    render={({ field }) => {
      const hasSelected = field.value.some((vt) => vt.isSelected)
      const bulkVis = getBulkVisibility(field.value)

      function handleBulkChange(value: string) {
        if (value === 'custom') return
        field.onChange(
          field.value.map((vt) => ({
            ...vt,
            visibilityOverride: vt.isSelected ? value : vt.visibilityOverride,
          })),
        )
      }

      function toggleCheckbox(idx: number, checked: boolean) {
        const updated = [...field.value]
        const current = updated[idx]
        if (current) {
          const inheritedVis =
            checked ?
              bulkVis === 'custom' ?
                'all'
              : bulkVis
            : current.visibilityOverride
          updated[idx] = { ...current, isSelected: checked, visibilityOverride: inheritedVis }
        }
        field.onChange(updated)
      }

      function updateVisibility(idx: number, value: string) {
        const updated = [...field.value]
        const current = updated[idx]
        if (current) {
          updated[idx] = { ...current, visibilityOverride: value }
        }
        field.onChange(updated)
      }

      return (
        <div className='overflow-hidden rounded-lg border border-border-medium bg-white'>
          {/* Section header */}
          <div className='border-b border-border-medium px-sp-3 py-sp-2'>
            <div className='flex items-center gap-sp-1'>
              <Heading level='h3'>Booking access settings</Heading>
              {hasSelected ?
                <svg width='16' height='16' viewBox='0 0 16 16' fill='#2a7065'>
                  <path d='M13.3 4.3a1 1 0 0 1 0 1.4l-6 6a1 1 0 0 1-1.4 0l-3-3a1 1 0 1 1 1.4-1.4L6.5 9.6l5.3-5.3a1 1 0 0 1 1.4 0z' />
                </svg>
              : null}
            </div>
          </div>

          <div className='px-sp-3 pb-sp-3 pt-sp-2'>
            {(
              licenseForm.formState.errors.visitTypes &&
              typeof licenseForm.formState.errors.visitTypes.message === 'string'
            ) ?
              <p className='mb-sp-2 text-sm text-text-error'>{licenseForm.formState.errors.visitTypes.message}</p>
            : null}

            {/* "All services" header row */}
            <div className='flex items-center justify-between border-b border-border-dark pb-sp-2'>
              <span className='text-sm font-semibold tracking-[0.12px] text-text-dark'>All services</span>
              <VisibilityButtonGroup value={bulkVis} onChange={handleBulkChange} showCustom />
            </div>

            {/* Service rows */}
            <div className='max-h-[400px] overflow-y-auto'>
              {field.value.map((vt, idx) => (
                <div
                  key={vt.visitTypeId}
                  className='flex items-center justify-between border-b border-border-dark py-sp-2 last:border-b-0'>
                  <div className='flex items-center gap-[12px]'>
                    <input
                      type='checkbox'
                      checked={vt.isSelected}
                      onChange={(e) => toggleCheckbox(idx, e.target.checked)}
                      className='size-6 shrink-0 rounded accent-[#178199]'
                    />
                    <div className='flex flex-col'>
                      <span className='text-sm font-semibold tracking-[0.12px] text-text-dark'>{vt.serviceName}</span>
                      <span className='text-xs tracking-[0.12px] text-text-light'>{vt.visitName}</span>
                    </div>
                  </div>
                  <VisibilityButtonGroup value={vt.visibilityOverride} onChange={(val) => updateVisibility(idx, val)} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    }}
  />
)
